import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { WorkerContext } from '../App';
import { useInitialization } from '../context/InitializationContext';

interface BackgroundInitializerProps {
  children: React.ReactNode;
}

const BackgroundInitializer: React.FC<BackgroundInitializerProps> = ({ children }) => {
  // const { isAuthenticated } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const [webglUnsupported, setWebglUnsupported] = useState(false);
  const workerRef = useRef<Worker | null>(null);
  const { setProgress, setIsInitialized: setContextInitialized } = useInitialization();
  const initializationComplete = useRef(false);
  const currentProgress = useRef(0);
  const messageIdCounter = useRef(0);
  const workerInitAttempts = useRef(0);
  const maxWorkerInitAttempts = 3;
  const workerInitTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const initializeApp = async () => {
      if (initializationComplete.current) {
        console.log('Initialization already completed, skipping');
        return;
      }

      try {
        // console.log('Starting initialization process...');
        currentProgress.current = 5;
        setProgress(5);

        // Create worker
        // console.log('Creating worker...');
        let worker: Worker | null = null;
        try {
          // worker = new Worker(new URL('../openCascadeWorker.ts', import.meta.url));
          // console.log('Worker creation skipped (OpenCascade not used)');
        } catch (error) {
          console.error('Error creating worker:', error);
          throw new Error('Failed to create worker: ' + (error instanceof Error ? error.message : String(error)));
        }

        workerRef.current = worker;
        currentProgress.current = 10;
        setProgress(10);

        // if (worker) {
        //   worker.onmessage = (event) => {
        //     console.log('Received message from worker:', event.data);
        //   };
        //   worker.onerror = (error) => {
        //     console.error('Worker error:', error);
        //   };
        // }

        const workerInitPromise = new Promise((resolve, reject) => {
          if (!worker) {
            resolve(true);
            return;
          }

          if (workerInitTimeout.current) {
            clearTimeout(workerInitTimeout.current);
          }

          workerInitTimeout.current = setTimeout(() => {
            console.error('Worker initialization timed out');
            reject(new Error('Worker initialization timeout'));
          }, 15000);

          worker.onmessage = ({ data }) => {
            console.log('Worker message received:', data);

            if (data.type === 'status') {
              switch (data.status) {
                case 'initializing':
                  console.log('Worker is initializing');
                  currentProgress.current = 25;
                  setProgress(25);
                  break;
                // case 'loading-opencascade':
                //   console.log('Worker is loading OpenCascade');
                //   currentProgress.current = 75;
                //   setProgress(75);
                //   break;
                case 'ready':
                  console.log('Worker is ready');
                  if (workerInitTimeout.current) {
                    clearTimeout(workerInitTimeout.current);
                    workerInitTimeout.current = null;
                  }
                  resolve(true);
                  break;
                case 'error':
                  console.error('Worker reported error:', data.message);
                  if (workerInitTimeout.current) {
                    clearTimeout(workerInitTimeout.current);
                    workerInitTimeout.current = null;
                  }
                  reject(new Error(data.message || 'Worker initialization failed'));
                  break;
              }
            }
          };

          try {
            // worker.postMessage({ type: 'init', messageId: messageIdCounter.current++ });
            console.log('Init message to worker skipped');
          } catch (error) {
            console.error('Error sending init message to worker:', error);
            if (workerInitTimeout.current) {
              clearTimeout(workerInitTimeout.current);
              workerInitTimeout.current = null;
            }
            reject(new Error('Failed to send message to worker: ' + (error instanceof Error ? error.message : String(error))));
          }
        });

        await Promise.all([
          // WebGL check با Three.js
          new Promise<void>((resolve) => {
            // console.log('Checking WebGL support using Three.js');
            const isAvailable = (() => {
              try {
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
                return !!context;
              } catch (e) {
                return false;
              }
            })();

            if (isAvailable) {
              // console.log('WebGL is available');
              if (currentProgress.current < 30) {
                currentProgress.current = 30;
                setProgress(30);
              }
            } else {
              console.warn('WebGL is not available');
              setWebglUnsupported(true);
            }
            resolve();
          }),

          workerInitPromise.catch(error => {
            console.error('Worker initialization failed:', error);
            return false;
          })
        ]);

        // console.log('Initialization complete');
        setProgress(100);
        setIsInitialized(true);
        setContextInitialized(true);
        initializationComplete.current = true;

      } catch (error) {
        console.error('Initialization failed:', error);
        setProgress(0);
        setIsInitialized(false);
        setContextInitialized(false);

        if (workerRef.current) {
          workerRef.current.terminate();
          workerRef.current = null;
        }

        if (workerInitAttempts.current < maxWorkerInitAttempts) {
          workerInitAttempts.current++;
          console.log(`Retrying initialization (attempt ${workerInitAttempts.current}/${maxWorkerInitAttempts})...`);
          setTimeout(initializeApp, 1000);
        } else {
          console.error('Max initialization attempts reached, giving up');
          setProgress(100);
          setIsInitialized(true);
          setContextInitialized(true);
          initializationComplete.current = true;
        }
      }
    };

    initializeApp();

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      if (workerInitTimeout.current) {
        clearTimeout(workerInitTimeout.current);
        workerInitTimeout.current = null;
      }
    };
  }, [setProgress, setContextInitialized]);

  return (
    <WorkerContext.Provider value={{ worker: workerRef.current }}>
      {webglUnsupported && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '24px 32px',
              borderRadius: '8px',
              maxWidth: '450px',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              fontFamily: 'Arial, Helvetica, sans-serif',
            }}
          >
            <h2 style={{ marginBottom: '16px', color: '#f44336' }}>
              ⚠️ WebGL Not Supported
            </h2>
            <p style={{ marginBottom: '16px' }}>
              Your browser does not support WebGL. Some 3D content or meshes may not be displayed correctly.
            </p>
            <p>Please try a different browser or device.</p>
          </div>
        </div>
      )}
      {children}
    </WorkerContext.Provider>
  );
};

export default BackgroundInitializer;
